// src/modules/competitions/components/ParticipantStatus.tsx

import React from 'react';
import Card from '../../../shared/components/ui/Card';
import { ParticipantStatus as ParticipantStatusType, NextAction, CompetitionSubmission } from '../types';
import { competitionService } from '../services/competitionService';
import Button from '../../../shared/components/ui/Button';
import '../styles/participantStatus.css';

interface ParticipantStatusProps {
  status: ParticipantStatusType;
  nextActions: NextAction[];
  submissions?: CompetitionSubmission[];
  onAction?: (action: string, data?: any) => void;
}

const ParticipantStatus: React.FC<ParticipantStatusProps> = ({
  status,
  nextActions,
  submissions = [],
  onAction
}) => {
  const progress = competitionService.calculateProgress(
    status.uploaded_images,
    status.required_images
  );

  const getStageText = (stage: string) => {
    switch (stage) {
      case 'pending_terms':
        return 'Pendiente: Aceptar términos';
      case 'pending_payment':
        return 'Pendiente: Realizar pago';
      case 'ready_to_submit':
        return 'Listo para enviar imágenes';
      case 'submitting':
        return 'Enviando imágenes';
      case 'evaluation_pending':
        return 'En evaluación';
      default:
        return 'Estado desconocido';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'pending_terms':
      case 'pending_payment':
        return '#f39c12';
      case 'ready_to_submit':
      case 'submitting':
        return '#3498db';
      case 'evaluation_pending':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#e74c3c';
      case 'medium':
        return '#f39c12';
      case 'low':
        return '#95a5a6';
      default:
        return '#3498db';
    }
  };

  const handleActionClick = (action: NextAction) => {
    if (onAction) {
      onAction(action.action, action);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="participant-status">
      <Card className="status-overview" variant="elevated">
        <div className="status-header">
          <h3>Estado de Participación</h3>
          <div 
            className="status-badge"
            style={{ backgroundColor: getStageColor(status.current_stage) }}
          >
            {getStageText(status.current_stage)}
          </div>
        </div>

        <div className="participant-info">
          <div className="info-row">
            <span className="info-label">Competencia:</span>
            <span className="info-value">{status.event_title}</span>
          </div>
          
          <div className="info-row">
            <span className="info-label">Categoría:</span>
            <span className="info-value">{status.category_name}</span>
          </div>
          
          <div className="info-row">
            <span className="info-label">Número de participante:</span>
            <span className="info-value">{status.participant_number || 'Pendiente'}</span>
          </div>
          
          <div className="info-row">
            <span className="info-label">Fecha de registro:</span>
            <span className="info-value">{formatDate(status.registration_date)}</span>
          </div>
        </div>
      </Card>

      <Card className="progress-card" variant="elevated">
        <h4>Progreso de Envío de Imágenes</h4>
        
        <div className="progress-stats">
          <div className="progress-numbers">
            <span className="progress-current">{status.uploaded_images}</span>
            <span className="progress-separator"> / </span>
            <span className="progress-total">{status.required_images}</span>
            <span className="progress-label"> imágenes</span>
          </div>
          
          <div className="progress-percentage">
            {progress.percentage.toFixed(1)}% completado
          </div>
        </div>

        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>

        {progress.completed ? (
          <div className="progress-message success">
            ¡Todas las imágenes han sido enviadas!
          </div>
        ) : (
          <div className="progress-message">
            Faltan {progress.remaining} imagen{progress.remaining !== 1 ? 's' : ''} por enviar
          </div>
        )}
      </Card>

      <Card className="payment-status" variant="elevated">
        <h4>Estado de Pago</h4>
        
        <div className="payment-info">
          <span className="payment-label">Estado:</span>
          <span 
            className="payment-value"
            style={{ 
              color: competitionService.getStatusColor(status.payment_status) 
            }}
          >
            {competitionService.getPaymentStatusText(status.payment_status)}
          </span>
        </div>

        <div className="terms-info">
          <span className="terms-label">Términos y condiciones:</span>
          <span 
            className="terms-value"
            style={{ 
              color: status.terms_accepted ? '#27ae60' : '#e74c3c' 
            }}
          >
            {status.terms_accepted ? 'Aceptados' : 'Pendientes'}
          </span>
        </div>
      </Card>

      {nextActions.length > 0 && (
        <Card className="next-actions" variant="elevated">
          <h4>Acciones Pendientes</h4>
          
          <div className="actions-list">
            {nextActions.map((action, index) => (
              <div 
                key={index}
                className="action-item"
                style={{ borderLeftColor: getPriorityColor(action.priority) }}
              >
                <div className="action-content">
                  <h5 className="action-title">{action.title}</h5>
                  
                  <div className="action-meta">
                    <span 
                      className="action-priority"
                      style={{ color: getPriorityColor(action.priority) }}
                    >
                      Prioridad {action.priority}
                    </span>
                    
                    {action.remaining && (
                      <span className="action-remaining">
                        {action.remaining} restante{action.remaining !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <Button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleActionClick(action)}
                >
                  Realizar
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {submissions.length > 0 && (
        <Card className="submissions-summary" variant="elevated">
          <h4>Imágenes Enviadas</h4>
          
          <div className="submissions-list">
            {submissions.map((submission) => (
              <div key={submission.id} className="submission-item">
                <div className="submission-info">
                  <h6 className="submission-type">{submission.image_type_name}</h6>
                  <p className="submission-filename">{submission.file_name}</p>
                  <p className="submission-date">
                    Enviada el {formatDate(submission.uploaded_at)}
                  </p>
                </div>
                
                <div className="submission-status">
                  {submission.is_valid ? (
                    <span className="status-valid">✓ Válida</span>
                  ) : (
                    <span className="status-invalid">✗ Inválida</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="additional-info" variant="flat">
        <h4>Información Importante</h4>
        
        <div className="info-items">
          <div className="info-item">
            <span className="info-icon">💡</span>
            <p>
              Puedes modificar tus imágenes enviadas hasta la fecha límite de la competencia.
            </p>
          </div>
          
          <div className="info-item">
            <span className="info-icon">⚠️</span>
            <p>
              Asegúrate de que todas las imágenes cumplan con los requisitos técnicos especificados.
            </p>
          </div>
          
          <div className="info-item">
            <span className="info-icon">📧</span>
            <p>
              Te notificaremos por email sobre cualquier actualización importante de tu participación.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ParticipantStatus;